<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/task-templates';
    import { Head } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateTaskTemplate from '@/components/task-templates/CreateTaskTemplate.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { BreadcrumbItem } from '@/types';

    const { hasPermission } = usePermissions();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.administration',
        },
        {
            title: 'task-templates.page_header',
            href: route('v2.task-templates.index'),
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.TASK_TEMPLATES],
        queryFn: getTaskTemplates,
    });

    async function getTaskTemplates() {
        const { data } = await axios.get(route('v2.task-templates.table'));
        return data.data;
    }
</script>

<template>
    <Head :title="$t('task-templates.page_header')" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <PageCard v-if="hasPermission(Permission.TASK_TEMPLATES_VIEW)">
            <DataTable
                :table-key="QueryKey.TASK_TEMPLATES"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :default-column-visibility="{
                    id: false,
                }"
                :default-sorting="[
                    {
                        id: 'name',
                        desc: false,
                    },
                ]"
            >
                <template #title>
                    <CardTitle>
                        {{ $t('task-templates.page_header') }}
                    </CardTitle>
                </template>
                <template #actions>
                    <DataTableToolbarActions name="task-templates">
                        <CreateTaskTemplate
                            v-if="hasPermission(Permission.TASK_TEMPLATES_CREATE)"
                        />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
